import { useEffect, useRef, useState } from 'react';

export const useWakeLock = (enabled: boolean) => {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Function to request wake lock
    const requestWakeLock = async () => {
      if (!enabled) return;
      
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('Wake Lock is active');
          
          wakeLockRef.current.addEventListener('release', () => {
            console.log('Wake Lock was released');
          });
        } else {
          console.warn('Wake Lock API not supported');
        }
      } catch (err) {
        console.error('Failed to request Wake Lock:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    };

    // Function to release wake lock
    const releaseWakeLock = async () => {
      if (wakeLockRef.current) {
        try {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
          console.log('Wake Lock released manually');
        } catch (err) {
          console.error('Failed to release Wake Lock:', err);
        }
      }
    };

    // Handle visibility change (lock is automatically released when visibility changes)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && enabled) {
        requestWakeLock();
      }
    };

    if (enabled) {
      requestWakeLock();
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } else {
      releaseWakeLock();
    }

    return () => {
      releaseWakeLock();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);

  return { error };
};
